import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useGetApiV1MissionsBoard,
  usePostApiV1MissionsStrandedOnBehalf,
  usePostApiV1MissionsSubMissions,
} from '@/shared/api/generated/missions/missions';
import type {
  GetApiV1MissionsBoardParams,
  MissionListItemDto,
} from '@/shared/api/generated/model';
import { MissionStatus, MissionType } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { formatDateTime, formatDuration } from '@/shared/lib/format/dates';
import { QueryError } from '@/shared/ui/QueryError';
import { Chip, toneForMissionStatus } from '@/shared/ui/Chip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { MissionCoverage } from '@/features/missions/ui/MissionCoverage';
import { MissionActions } from '@/features/missions/ui/MissionActions';
import { CreateSubMissionDialog } from '@/features/missions/ui/CreateSubMissionDialog';
import { ReportStrandedDialog } from '@/features/missions/ui/ReportStrandedDialog';
import { Button } from '@/shared/ui/Button';
import { SystemRoles, useRoles } from '@/features/auth/model/use-roles';
import { problemDetail } from '@/shared/api/problem';
import { LiveEvery, liveEvery } from '@/shared/lib/live';

/**
 * The control room's board (§23.1: Control follows missions, ends them, adds members).
 *
 * Reads `/missions/board`, not `/missions`. The volunteer route answers "what can I join" and hides
 * drafts on purpose; a board that hid them would hide exactly the missions waiting on a decision.
 */
export function MissionBoard() {
  const { t, locale } = useLocale();
  const { hasAny } = useRoles();
  const [status, setStatus] = useState<MissionStatus | 'all'>('all');
  const [type, setType] = useState<MissionType | 'all'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const params: GetApiV1MissionsBoardParams = {
    ...(status === 'all' ? {} : { status }),
    ...(type === 'all' ? {} : { type }),
  };

  const missions = useGetApiV1MissionsBoard(params, liveEvery(LiveEvery.work));

  // ⚠️ **Head of Control, matching the server exactly.** A desk operator opening the dialog would
  // fill a whole form and be told 403 by the button at the bottom of it; §23.3 has this family
  // created BY the association, which is the same authority that publishes a search without a vote.
  const mayOpen = hasAny(SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator);

  // ⚠️ Desk Control too, not only the Head: §23.1 puts them on the telephone, and this is the call
  // they answer. The server's policy is the same three roles.
  const mayReportStranded = hasAny(
    SystemRoles.DeskControl,
    SystemRoles.HeadOfControl,
    SystemRoles.SystemAdministrator,
  );

  const reportStranded = usePostApiV1MissionsStrandedOnBehalf({
    mutation: {
      onSuccess: async () => {
        setReporting(false);
        setReportError(null);
        await missions.refetch();
      },
      onError: (cause: unknown) => setReportError(problemDetail(cause) ?? t.missions.actionFailed),
    },
  });

  const open = usePostApiV1MissionsSubMissions({
    mutation: {
      onSuccess: async () => {
        setOpening(false);
        setOpenError(null);
        await missions.refetch();
      },
      onError: (cause: unknown) => setOpenError(problemDetail(cause) ?? t.missions.actionFailed),
    },
  });

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-2xl font-bold tracking-tight">{t.missions.heading}</h1>
          <p className="text-sm text-muted-foreground">{t.missions.lede}</p>
        </div>

        {/* ⚠️ **The only mission anybody CREATES from a screen.** A search arrives as a report and a
            rescue creates itself; a sub-mission is the association deciding to run something, so
            this is the one board with a button that makes a mission rather than acting on one. */}
        <div className="flex flex-wrap gap-2">
          {/* ⚠️ **The one search a screen opens.** A stranded person presses «أنا عالق» in the app;
              somebody who telephones instead has nobody to press it for them, and until this button
              existed the operator's only route was to talk a person stuck in a wadi through
              installing an application. Control's, like the board itself. */}
          {mayReportStranded ? (
            <Button onClick={() => setReporting(true)}>{t.missions.strandedCreate}</Button>
          ) : null}

          {mayOpen ? (
            <Button variant="ghost" onClick={() => setOpening(true)}>
              {t.missions.subCreate}
            </Button>
          ) : null}
        </div>
      </header>

      <ReportStrandedDialog
        open={reporting}
        onOpenChange={(next) => {
          setReporting(next);
          if (!next) setReportError(null);
        }}
        busy={reportStranded.isPending}
        serverError={reportError}
        onConfirm={(command) => reportStranded.mutate({ data: command })}
      />

      <CreateSubMissionDialog
        open={opening}
        onOpenChange={(next) => {
          setOpening(next);
          if (!next) setOpenError(null);
        }}
        busy={open.isPending}
        serverError={openError}
        onConfirm={(command) => open.mutate({ data: command })}
      />

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2" role="group" aria-label={t.missions.status}>
          <FilterChip
            label={t.missions.filterAll}
            active={status === 'all'}
            onClick={() => setStatus('all')}
          />
          {Object.values(MissionStatus).map((value) => (
            <FilterChip
              key={value}
              label={t.missionStatus[value]}
              active={status === value}
              onClick={() => setStatus(value)}
            />
          ))}
        </div>

        <div className="flex flex-wrap gap-2" role="group" aria-label={t.missions.type}>
          <FilterChip
            label={t.missions.filterAll}
            active={type === 'all'}
            onClick={() => setType('all')}
          />
          {Object.values(MissionType).map((value) => (
            <FilterChip
              key={value}
              label={t.missionType[value]}
              active={type === value}
              onClick={() => setType(value)}
            />
          ))}
        </div>
      </div>

      {missions.isPending ? (
        <p className="text-sm text-muted-foreground">{t.app.loading}</p>
      ) : missions.isError ? (
        <QueryError error={missions.error} onRetry={() => void missions.refetch()} />
      ) : missions.data.items.length === 0 ? (
        <p className="rounded-md border border-border bg-surface px-4 py-6 text-center text-sm text-muted-foreground">
          {t.missions.empty}
        </p>
      ) : (
        <div className="rounded-lg border border-border bg-surface">
          <Table className="min-w-3xl">
            <TableHeader>
              <TableRow className="bg-surface-elevated text-xs uppercase tracking-wider text-muted-foreground">
                <TableHead scope="col">{t.missions.title}</TableHead>
                <TableHead scope="col">{t.missions.type}</TableHead>
                <TableHead scope="col">{t.missions.status}</TableHead>
                <TableHead scope="col">{t.missions.region}</TableHead>
                <TableHead scope="col">{t.missions.area}</TableHead>
                <TableHead scope="col">{t.missions.members}</TableHead>
                <TableHead scope="col">{t.missions.elapsed}</TableHead>
                <TableHead scope="col">{t.missions.coverageHeading}</TableHead>
                <TableHead scope="col">{t.missions.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {missions.data.items.map((mission) => (
                <Row
                  key={mission.missionId}
                  mission={mission}
                  params={params}
                  locale={locale}
                  expanded={expanded === mission.missionId}
                  onToggle={() =>
                    setExpanded(expanded === mission.missionId ? null : mission.missionId)
                  }
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground hover:border-primary'
      }`}
    >
      {label}
    </button>
  );
}

function Row({
  mission,
  params,
  locale,
  expanded,
  onToggle,
}: {
  mission: MissionListItemDto;
  params: GetApiV1MissionsBoardParams;
  locale: 'ar' | 'en';
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useLocale();

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">
          {/* The board answers "what needs a decision"; the detail answers "on what evidence".
              §23.6 credits participation from there, so every row needs a way in. */}
          <Link
            to="/missions/$missionId"
            params={{ missionId: mission.missionId }}
            className="text-primary underline underline-offset-4"
          >
            {mission.title}
          </Link>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {label(mission.type, t.missionType)}
        </TableCell>
        <TableCell>
          <Chip tone={toneForMissionStatus(mission.status)}>
            {label(mission.status, t.missionStatus)}
          </Chip>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {mission.region || t.missions.noRegion}
          {mission.city ? ` — ${mission.city}` : ''}
        </TableCell>
        <TableCell className="tabular text-muted-foreground">
          {mission.areaKm2.toFixed(1)} {t.missions.areaUnit}
        </TableCell>
        <TableCell className="tabular text-muted-foreground">{mission.memberCount}</TableCell>
        <TableCell className="tabular text-muted-foreground">
          {/* The figure that ranks urgency: how long the person has been missing or stranded. */}
          {mission.elapsedSinceIncident
            ? formatDuration(mission.elapsedSinceIncident, {
                day: t.missions.unitDay,
                hour: t.missions.unitHour,
                minute: t.missions.unitMinute,
              })
            : formatDateTime(mission.createdAt, locale)}
        </TableCell>
        <TableCell>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className="text-start text-primary underline underline-offset-4"
          >
            {t.missions.coverageHeading}
          </button>
        </TableCell>
        <TableCell>
          <MissionActions mission={mission} params={params} />
        </TableCell>
      </TableRow>

      {/* Coverage is fetched only when a row is opened. It runs ST_Union over every track point in the
          mission (§9), so fetching it for a page of twenty would make the board's own load a series of
          spatial aggregations nobody asked for. */}
      {expanded ? (
        <TableRow className="bg-surface-elevated/40">
          <TableCell colSpan={9} className="py-4">
            <MissionCoverage missionId={mission.missionId} />
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

/**
 * The API sends enum NAMES as strings. An unknown key falls back to the raw value rather than to an
 * empty cell: if the server adds a status before this panel knows about it, showing `Suspended` is
 * honest and showing nothing hides the row's meaning.
 */
function label(value: string, labels: Record<string, string>): string {
  return labels[value] ?? value;
}
